import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { X, Check, ArrowRight, CloudOff, RotateCcw, LayoutGrid } from "lucide-react";
import { api } from "@/lib/api";
import { pct } from "@/lib/quiz";
import { QuizImage } from "@/components/quiz/QuizImage";

const LETTERS = ["A", "B", "C", "D"];
const shuffle = (a) => a.map((v) => [Math.random(), v]).sort((x, y) => x[0] - y[0]).map((x) => x[1]);

export const useOfflinePack = () =>
  useQuery({ queryKey: ["offline-pack"], queryFn: () => api.get("/quizzes/offline-pack").then((r) => r.data), staleTime: 10 * 60_000 });

function build(pack, topic) {
  const pool = pack.questions.filter((q) => topic === "mixed" || q.category === topic);
  return shuffle(pool).slice(0, 10).map((q) => {
    const options = shuffle([q.correct, ...q.wrong]);
    return { ...q, options, correct_index: options.indexOf(q.correct) };
  });
}

export function NoXpNote({ className = "" }) {
  return (
    <div className={`flex items-center gap-2 text-xs text-[#F59E0B] ${className}`} data-testid="practice-no-xp">
      <CloudOff size={13} className="shrink-0" /> Offline practice · scores don't count and no XP is earned
    </div>
  );
}

function Results({ qs, answers, again }) {
  const navigate = useNavigate();
  const score = qs.filter((q, i) => answers[i] === q.correct_index).length;
  return (
    <div className="space-y-5 rise" data-testid="practice-results">
      <section className="card p-6 text-center">
        <div className="eyebrow mb-2">Offline practice</div>
        <div className="font-display text-6xl font-bold leading-none" data-testid="practice-score">{score}<span className="text-[#52525B]">/{qs.length}</span></div>
        <div className="text-lg font-semibold mt-2">{pct(score, qs.length)}%</div>
        <NoXpNote className="justify-center mt-4" />
      </section>
      <div className="space-y-3">
        {qs.map((q, i) => {
          const right = answers[i] === q.correct_index;
          return (
            <div key={q.id} className="card p-4" data-testid={`practice-review-${i}`}>
              <div className="text-[11px] uppercase tracking-wider text-[#71717A]">{q.category_label}</div>
              <p className="font-semibold leading-snug mt-1">{q.question}</p>
              {!right && <p className="text-sm mt-2 text-[#FCA5A5]"><span className="text-[#71717A]">You: </span>{q.options[answers[i]]}</p>}
              <p className="text-sm mt-1.5 text-[#6EE7B7]"><span className="text-[#71717A]">Answer: </span>{q.correct}</p>
              <p className="text-sm text-[#A1A1AA] mt-2 leading-relaxed">{q.explanation}</p>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <button data-testid="practice-again" onClick={again} className="btn-secondary"><RotateCcw size={16} /> Again</button>
        <button data-testid="practice-back" onClick={() => navigate("/quizzes")} className="btn-secondary"><LayoutGrid size={16} /> Topics</button>
      </div>
    </div>
  );
}

export default function OfflinePractice() {
  const { topic } = useParams();
  const navigate = useNavigate();
  const { data: pack } = useOfflinePack();
  const [round, setRound] = useState(0);
  const qs = useMemo(() => (pack ? build(pack, topic) : []), [pack, topic, round]); // eslint-disable-line react-hooks/exhaustive-deps
  const [answers, setAnswers] = useState([]);
  const [index, setIndex] = useState(0);
  const done = qs.length > 0 && answers.length === qs.length && index >= qs.length;

  const again = () => { setRound(round + 1); setAnswers([]); setIndex(0); window.scrollTo(0, 0); };
  const q = qs[index];
  const chosen = answers[index];

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-md mx-auto min-h-screen bg-[#0A0A0A] px-5 pt-8 pb-32">
        <header className="flex items-center gap-3 mb-3">
          <button data-testid="practice-close" onClick={() => navigate("/quizzes")} className="w-12 h-12 -ml-3 rounded-full flex items-center justify-center text-[#A1A1AA] hover:text-white" aria-label="Close"><X size={22} /></button>
          <div className="flex-1 text-sm font-semibold">Practice</div>
          {!done && qs.length > 0 && <span className="text-sm font-semibold tabular-nums" data-testid="practice-progress">{index + 1}/{qs.length}</span>}
        </header>
        {!done && <NoXpNote className="mb-6" />}
        {!pack && <div className="card h-72 animate-pulse" />}
        {pack && !qs.length && <div className="card p-6 text-sm text-[#A1A1AA]" data-testid="practice-empty">No questions saved for offline yet. Take some quizzes while online and they'll be ready here.</div>}
        {done && <Results qs={qs} answers={answers} again={again} />}
        {q && !done && (
          <div className="fade-in" key={`${round}-${index}`}>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#71717A] font-semibold mb-2">{q.category_label}</div>
            <QuizImage image={q.image} testId="practice-question-image" />
            <h2 className="text-xl font-semibold leading-snug mb-6" data-testid="practice-question">{q.question}</h2>
            <div className="space-y-2.5">
              {q.options.map((o, i) => {
                const cls = chosen == null ? "border-[#27272A] bg-[#111113] hover:border-[#3F3F46]" : i === q.correct_index ? "border-[#10B981] bg-[#10B981]/10" : i === chosen ? "border-[#EF4444] bg-[#EF4444]/10" : "border-[#1F1F23] opacity-50";
                return (
                  <button key={i} data-testid={`practice-option-${i}`} disabled={chosen != null} onClick={() => setAnswers(Object.assign([...answers], { [index]: i }))} className={`w-full min-h-[60px] text-left px-4 py-3.5 rounded-2xl border flex items-center gap-3.5 transition-[border-color,background-color,opacity] duration-150 ${cls}`}>
                    <span className="w-7 h-7 rounded-lg text-xs font-bold flex items-center justify-center shrink-0 bg-[#222225] text-[#A1A1AA]">
                      {chosen != null && i === q.correct_index ? <Check size={14} strokeWidth={3} className="text-[#10B981]" /> : chosen === i ? <X size={14} strokeWidth={3} className="text-[#EF4444]" /> : LETTERS[i]}
                    </span>
                    <span className="text-[15px] leading-snug">{o}</span>
                  </button>
                );
              })}
            </div>
            {chosen != null && (
              <div className={`mt-5 p-4 rounded-2xl border-l-2 fade-in ${chosen === q.correct_index ? "border-[#10B981] bg-[#10B981]/10" : "border-[#EF4444] bg-[#EF4444]/10"}`} data-testid="practice-feedback">
                <div className={`text-sm font-bold mb-1 ${chosen === q.correct_index ? "text-[#10B981]" : "text-[#EF4444]"}`}>{chosen === q.correct_index ? "Correct" : "Not quite"}</div>
                <p className="text-sm text-[#D4D4D8] leading-relaxed">{q.explanation}</p>
              </div>
            )}
          </div>
        )}
        {q && !done && chosen != null && (
          <div className="fixed bottom-0 inset-x-0 z-40 max-w-md mx-auto px-5 pt-3 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A] to-transparent" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 1.25rem)" }}>
            <button data-testid="practice-next" onClick={() => { setIndex(index + 1); window.scrollTo(0, 0); }} className="btn-primary">
              {index < qs.length - 1 ? <>Next question <ArrowRight size={18} /></> : <>See results <ArrowRight size={18} /></>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
